/**
 * What counts as "apparel" for the storefront's apparel offers — the Bundle &
 * Save ladder and the Hydrate perk. Both are enforced at checkout by the
 * apparel-bundle-discount Shopify Function (apps/shopify-functions), which
 * carries an identical copy of these rules; a parity test keeps them in step.
 *
 * The rule is deliberately code, not CMS: product types decide, a short list
 * of exclusions handles the odd ones out, and there's nothing to configure.
 */

export const APPAREL_TYPE_FRAGMENTS = [
	'tshirt',
	'tee',
	'shirt',
	'hoodie',
	'hoody',
	'sweatshirt',
	'crewneck',
	'pullover',
	'polo',
	'longsleeve',
	'jacket',
	'vest',
	'trunk',
	'short',
	'pant',
	'jogger',
	'apparel',
] as const;

/** Types that never count, even when a fragment above would match ("Apparel Bundle", "Polo Bundle"). */
export const NON_APPAREL_TYPE_FRAGMENTS = [
	'electrolyte',
	'stickpack',
	'hydrate',
	'giftcard',
	'protection',
	'bundle',
] as const;

/**
 * Products that never count however they're typed: a handle (exact) or a
 * phrase from the title. The Standard tee/hoodie are the blanks; "Toolbox"
 * is a kit.
 */
export const EXCLUDED_APPAREL_PRODUCTS = [
	'the-standard-tee',
	'the-standard-hoodie',
	'standard-tee-black-4-for-3-copy',
	'Standard Tee',
	'Standard Hoodie',
	'Toolbox',
] as const;

/** Lower-case, punctuation-free, so "T-Shirt", "t shirt" and "Tshirt" compare equal. */
export function normalizeProductType(value: string | null | undefined): string {
	return (value ?? '').toLowerCase().replace(/[\s\-_&']+/g, '');
}

function normalizeKey(value: string | null | undefined): string {
	return (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function isApparelProductType(productType: string | null | undefined): boolean {
	const type = normalizeProductType(productType);
	if (!type) return false;
	if (NON_APPAREL_TYPE_FRAGMENTS.some((fragment) => type.includes(fragment))) return false;
	return APPAREL_TYPE_FRAGMENTS.some((fragment) => type.includes(fragment));
}

/**
 * A bundle of apparel sold as one product ("Apparel Bundle", "Polo Bundle").
 * Off the Bundle & Save ladder, since it is already bundle-priced, but still
 * apparel to the Hydrate perk, which takes its 10% off every apparel unit.
 * Mirror of `isApparelBundleType` in the Function.
 */
export function isApparelBundleProductType(productType: string | null | undefined): boolean {
	const type = normalizeProductType(productType);
	if (!type.includes('bundle')) return false;
	if (
		NON_APPAREL_TYPE_FRAGMENTS.some((fragment) => fragment !== 'bundle' && type.includes(fragment))
	) {
		return false;
	}
	return APPAREL_TYPE_FRAGMENTS.some((fragment) => type.includes(fragment));
}

/** Mirror of `isExcludedProduct` in the Function: exact handle, or a title phrase. */
export function isExcludedApparelProduct(product: {
	handle?: string | null;
	title?: string | null;
}): boolean {
	const handle = (product.handle ?? '').trim().toLowerCase();
	const title = normalizeKey(product.title);
	return EXCLUDED_APPAREL_PRODUCTS.some((entry) => {
		const lower = entry.toLowerCase();
		if (handle && lower === handle) return true;
		const key = normalizeKey(entry);
		return key.length > 0 && title.length > 0 && title.includes(key);
	});
}

/** On the ladder: an apparel product type that isn't on the exclusion list. */
export function isApparelProduct(product: {
	handle?: string | null;
	title?: string | null;
	productType: string | null | undefined;
}): boolean {
	return isApparelProductType(product.productType) && !isExcludedApparelProduct(product);
}
